// FILE: TenthExamFromTemplate.tsx
import React, { useState, useCallback, memo, useRef, useEffect } from "react";
import {
  Paper,
  Box,
  Typography,
  Grid,
  Input,
  FormControlLabel,
  Checkbox,
  ThemeProvider,
  createTheme,
  Button,
  CircularProgress,
} from "@mui/material";
import html2canvas from "html2canvas";
// @ts-ignore
import html2pdf from "html2pdf.js";
import { Download, Image } from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import {
  convertDateToHindiWords,
  formatDateFromForm,
} from "../../utils/dateToWordsHindi";
import { useStudentPhoto, useStudentSignature } from "../../hooks/useStudents";
import { useSubjectsByClassId } from "../../hooks/useSubjects";
import { useSchools } from "../../hooks/useSchools";
import { useAuth } from "../../contexts/AuthContext";

import { useSchoolIdForAdmin } from "../../hooks/useSchoolIdForAdmin";

/** ----------------------------------------------------------------
 *  Print-friendly theme
 *  ---------------------------------------------------------------- */
const theme = createTheme({
  typography: {
    fontFamily: `"Noto Sans Devanagari","Nirmala UI","Mangal","Hind","Arial",sans-serif`,
  },
});

/** ----------------------------------------------------------------
 *  Small reusable atoms
 *  ---------------------------------------------------------------- */

// Utility component to render static text boxes that always shape Devanagari correctly
const PrintText: React.FC<{ children: React.ReactNode; sx?: any }> = ({
  children,
  sx,
}) => (
  <Box
    sx={{
      fontFamily: `"Noto Sans Devanagari","Nirmala UI","Mangal","Hind","Arial",sans-serif`,
      fontFeatureSettings: "'liga' 1, 'clig' 1, 'kern' 1",
      WebkitFontSmoothing: "antialiased",
      whiteSpace: "pre-wrap",
      lineHeight: 1.2,

      ...sx,
    }}
  >
    {children}
  </Box>
);
interface RowProps {
  label: React.ReactNode;
  labelWidth?: string;
}
const Row: React.FC<RowProps> = memo(({ label, labelWidth = "130mm" }) => (
  <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
    <Box sx={{ width: labelWidth, flexShrink: 0 }}>
      <Label sx={{ fontSize: "13px", fontWeight: "bold" }}>{label}</Label>
    </Box>
  </Box>
));

interface LabelProps {
  children: React.ReactNode;
  sx?: any;
}
const Label: React.FC<LabelProps> = memo(({ children, sx = {} }) => (
  <Typography
    sx={{
      fontSize: "13px",
      fontWeight: "bold",
      lineHeight: 1,
      ...sx,
    }}
  >
    {children}
  </Typography>
));

interface CellProps {
  width?: string;
  height?: string;
  children?: React.ReactNode;
  sx?: any;
}
const Cell: React.FC<CellProps> = memo(
  ({ width = "6mm", height = "6mm", children, sx = {} }) => (
    <Box
      sx={{
        width,
        height,
        border: "1px solid #000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "10px",
        fontWeight: "bold",
        ...sx,
      }}
    >
      {children}
    </Box>
  )
);

interface CharBoxesProps {
  count: number;
  value: string;
  onChange?: (value: string) => void;
  boxSizeMm?: number;
  gapMm?: number;
  printMode?: boolean;
}
const CharBoxes: React.FC<CharBoxesProps> = memo(
  ({ count, value, onChange, boxSizeMm = 6, gapMm = 0, printMode = false }) => {
    const sanitizeInput = useCallback((input: string) => {
      return input
        .toUpperCase()
        .replace(/[^A-Z0-9\s.\-@]/g, "")
        .slice(0, 1); // Only take the first character for single character input
    }, []);

    const handleChange = useCallback(
      (index: number, inputValue: string) => {
        if (!onChange) return;

        // Get the current value array
        const currentValue = value.split("");

        // If input is empty, clear the box
        if (inputValue === "") {
          currentValue[index] = "";
        } else {
          // Sanitize and take only the last character typed
          const sanitized = sanitizeInput(inputValue);
          if (sanitized.length > 0) {
            currentValue[index] = sanitized;
          }
        }

        // Update the value and pad with spaces
        const newValue = currentValue.join("").padEnd(count, " ");
        onChange(newValue);
      },
      [onChange, sanitizeInput, value, count]
    );

    const handleKeyDown = useCallback(
      (index: number, e: React.KeyboardEvent) => {
        if (!onChange) return;

        // Handle backspace
        if (e.key === "Backspace") {
          const newValue = value.split("");
          newValue[index] = "";
          onChange(newValue.join("").padEnd(count, " "));
        }
        // Handle arrow keys for navigation
        else if (e.key === "ArrowLeft" && index > 0) {
          const nextInput = e.currentTarget.parentElement?.children[
            index - 1
          ] as HTMLInputElement;
          nextInput?.focus();
        } else if (e.key === "ArrowRight" && index < count - 1) {
          const nextInput = e.currentTarget.parentElement?.children[
            index + 1
          ] as HTMLInputElement;
          nextInput?.focus();
        }
      },
      [onChange, value, count]
    );

    const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
      // Select all text when focused for easy replacement
      e.target.select();
    }, []);

    const isReadOnly = !onChange;

    if (printMode || isReadOnly) {
      return (
        <Box sx={{ display: "flex", gap: `${gapMm}mm` }}>
          {Array.from({ length: count }, (_, i) => (
            <Box
              key={i}
              sx={{
                width: `${boxSizeMm}mm`,
                height: `${boxSizeMm}mm`,
                border: "1px solid #000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                fontWeight: "bold",
                backgroundColor: "white",
              }}
            >
              {(value[i] ?? "").toString().toUpperCase()}
            </Box>
          ))}
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", gap: `${gapMm}mm` }}>
        {Array.from({ length: count }, (_, i) => (
          <Input
            key={i}
            value={value[i] || ""}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onFocus={handleFocus}
            disabled={isReadOnly}
            sx={{
              width: `${boxSizeMm}mm`,
              height: `${boxSizeMm}mm`,
              border: "1px solid #000 !important",
              borderTop: "1px solid #000 !important",
              borderRight: "1px solid #000 !important",
              borderBottom: "1px solid #000 !important",
              borderLeft: "1px solid #000 !important",
              fontSize: "14px",
              lineHeight: 1,
              textAlign: "center",
              backgroundColor: isReadOnly ? "#f5f5f5" : "white",
              borderRadius: 0,
              boxSizing: "border-box",
              "& input": {
                textAlign: "center",
                padding: 0,
                backgroundColor: "transparent",
                color: isReadOnly ? "#666" : "inherit",
                cursor: isReadOnly ? "default" : "text",
                border: "none !important",
                outline: "none",
                width: "100%",
                height: "100%",
                boxSizing: "border-box",
              },
              "&:before": { display: "none !important" },
              "&:after": { display: "none !important" },
              "& fieldset": { display: "none !important" },
              "&:hover": {
                borderColor: isReadOnly ? "#000" : "#1976d2",
              },
              "&:focus-within": {
                borderColor: "#1976d2",
                borderWidth: "2px",
              },
            }}
            inputProps={{
              maxLength: 1,
              style: {
                textAlign: "center",
                fontSize: "14px",
                fontWeight: "bold",
              },
            }}
          />
        ))}
      </Box>
    );
  }
);

interface DateBoxesProps {
  value: { day: string; month: string; year: string };
  onChange?: (value: { day: string; month: string; year: string }) => void;
  printMode?: boolean;
}
const DateBoxes: React.FC<DateBoxesProps> = memo(
  ({ value, onChange, printMode = false }) => {
    const handleChange = useCallback(
      (field: "day" | "month" | "year", val: string) => {
        if (!onChange) return;
        const sanitized = val
          .replace(/[^0-9]/g, "")
          .slice(0, field === "year" ? 4 : 2);
        onChange({ ...value, [field]: sanitized });
      },
      [onChange, value]
    );

    if (printMode || !onChange) {
      return (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: "1mm", ml: "10mm" }}
        >
          <Box
            sx={{
              width: "8mm",
              height: "6mm",
              border: "1px solid #000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "white",
            }}
          >
            {value.day}
          </Box>
          <Typography sx={{ fontSize: "10px", mx: 0.5 }}>/</Typography>
          <Box
            sx={{
              width: "8mm",
              height: "6mm",
              border: "1px solid #000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "white",
            }}
          >
            {value.month}
          </Box>
          <Typography sx={{ fontSize: "10px", mx: 0.5 }}>/</Typography>
          <Box
            sx={{
              width: "16mm",
              height: "6mm",
              border: "1px solid #000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "white",
            }}
          >
            {value.year}
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "1mm",
          marginLeft: "10mm",
        }}
      >
        <Box
          sx={{
            width: "8mm",
            height: "6mm",
            border: "1px solid #000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "white",
          }}
        >
          <Input
            value={value.day}
            onChange={(e) => handleChange("day", e.target.value)}
            sx={{
              width: "100%",
              height: "100%",
              fontSize: "12px",
              textAlign: "center",
              backgroundColor: "transparent",
              "& input": {
                textAlign: "center",
                padding: 0,
                backgroundColor: "transparent",
                border: "none",
                outline: "none",
                width: "100%",
                height: "100%",
                fontSize: "12px",
                fontWeight: "bold",
              },
              "&:before": { display: "none" },
              "&:after": { display: "none" },
              "& fieldset": { display: "none" },
            }}
            inputProps={{ maxLength: 2 }}
          />
        </Box>
        <Typography sx={{ fontSize: "10px", mx: 0.5 }}>/</Typography>
        <Box
          sx={{
            width: "8mm",
            height: "6mm",
            border: "1px solid #000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "white",
          }}
        >
          <Input
            value={value.month}
            onChange={(e) => handleChange("month", e.target.value)}
            sx={{
              width: "100%",
              height: "100%",
              fontSize: "12px",
              textAlign: "center",
              backgroundColor: "transparent",
              "& input": {
                textAlign: "center",
                padding: 0,
                backgroundColor: "transparent",
                border: "none",
                outline: "none",
                width: "100%",
                height: "100%",
                fontSize: "12px",
                fontWeight: "bold",
              },
              "&:before": { display: "none" },
              "&:after": { display: "none" },
              "& fieldset": { display: "none" },
            }}
            inputProps={{ maxLength: 2 }}
          />
        </Box>
        <Typography sx={{ fontSize: "10px", mx: 0.5 }}>/</Typography>
        <Box
          sx={{
            width: "16mm",
            height: "6mm",
            border: "1px solid #000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "white",
          }}
        >
          <Input
            value={value.year}
            onChange={(e) => handleChange("year", e.target.value)}
            sx={{
              width: "100%",
              height: "100%",
              fontSize: "12px",
              textAlign: "center",
              backgroundColor: "transparent",
              "& input": {
                textAlign: "center",
                padding: 0,
                backgroundColor: "transparent",
                border: "none",
                outline: "none",
                width: "100%",
                height: "100%",
                fontSize: "12px",
                fontWeight: "bold",
              },
              "&:before": { display: "none" },
              "&:after": { display: "none" },
              "& fieldset": { display: "none" },
            }}
            inputProps={{ maxLength: 4 }}
          />
        </Box>
      </Box>
    );
  }
);

interface TickProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}
const Tick: React.FC<TickProps> = memo(({ label, checked, onChange }) => (
  <FormControlLabel
    control={
      <Checkbox
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        sx={{
          width: "6mm",
          height: "6mm",
          padding: 0,
          "& .MuiSvgIcon-root": { fontSize: "16px" },
        }}
      />
    }
    label={
      <Typography sx={{ fontSize: "12px", ml: 0.5, margin: "auto" }}>
        {label}
      </Typography>
    }
    sx={{ margin: 0, alignItems: "flex-start" }}
  />
));

interface MiniBoxesProps {
  count: number;
  value: string;
  onChange?: (value: string) => void;
  printMode?: boolean;
}
const MiniBoxes: React.FC<MiniBoxesProps> = ({
  count,
  value,
  onChange,
  printMode = false,
}) => {
  if (printMode || !onChange) {
    return (
      <Box sx={{ display: "flex", gap: "0.5mm" }}>
        {Array.from({ length: count }, (_, i) => (
          <Box
            key={i}
            sx={{
              width: "4mm",
              height: "4mm",
              border: "1px solid #000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "8px",
              fontWeight: "bold",
              backgroundColor: "white",
            }}
          >
            {(value[i] ?? "").toString().toUpperCase()}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: "0.5mm" }}>
      {Array.from({ length: count }, (_, i) => (
        <Input
          key={i}
          value={value[i] || ""}
          onChange={(e) => {
            if (!onChange) return;
            const newValue = value.split("");
            newValue[i] = e.target.value.slice(0, 1).toUpperCase();
            onChange(newValue.join("").slice(0, count));
          }}
          sx={{
            width: "4mm",
            height: "4mm",
            border: "1px solid #000 !important",
            borderTop: "1px solid #000 !important",
            borderRight: "1px solid #000 !important",
            borderBottom: "1px solid #000 !important",
            borderLeft: "1px solid #000 !important",
            fontSize: "8px",
            textAlign: "center",
            borderRadius: 0,
            boxSizing: "border-box",
            "& input": {
              textAlign: "center",
              padding: 0,
              border: "none !important",
              outline: "none",
              width: "100%",
              height: "100%",
              boxSizing: "border-box",
            },
            "&:before": { display: "none !important" },
            "&:after": { display: "none !important" },
            "& fieldset": { display: "none !important" },
          }}
          inputProps={{ maxLength: 1 }}
        />
      ))}
    </Box>
  );
};

/** ----------------------------------------------------------------
 *  Types
 *  ---------------------------------------------------------------- */
interface StudentData {
  id: number;
  applicationNo: string;
  rollNumber: string;
  enrolmentNo: string;
  studentName: string;
  fatherName: string;
  motherName: string;
  dateOfBirth: string | number[];
  gender: string;
  medium: string;
  caste: string;
  subCaste: string;
  casteCertificate: string;
  religion: string;
  sssmid: string;
  scholarNo: string;
  penNo: string;
  apparId: string;
  aadharNo: string;
  houseNo: string;
  street: string;
  landmark: string;
  area: string;
  city: string;
  postOffice: string;
  district: string;
  state: string;
  pinCode: string;
  mobileNo: string;
  bankName: string;
  ifcCode: string;
  accountNo: string;
  annualIncome: string;
  section: string;
  subjects: string | string[];
  gmail: string;
  admissionDate: string | number[];
  admissionClass: string;
  schoolId: number;
  schoolClassId: number;
  sessionName: string;
  sessionId: number;
  className: string;
  userId?: number;
  status: boolean;
  studentPhoto?: string | File;
  photoUrl?: string | null;
  signatureUrl?: string | null;
  skipStudentMediaFetch?: boolean;
  cwsnCode: string; // CWSN Code field
  sambalKramank: string; // Sambal Kramank field
}

interface Props {
  studentData?: StudentData;
  /** When true we render a lean version for off-screen PDF capture */
  renderForPDF?: boolean;
}

/** ----------------------------------------------------------------
 *  Component
 *  ---------------------------------------------------------------- */
const TwelfthExamFormTemplate: React.FC<Props> = ({
  studentData,
  renderForPDF = false,
}) => {
  const templateRef = useRef<HTMLDivElement>(null);

  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [isDownloadingPdfFromImage, setIsDownloadingPdfFromImage] =
    useState(false);
  const [captureMode, setCaptureMode] = useState(false);
  const isPrint = captureMode || renderForPDF;

  const shouldFetchPhoto =
    !!studentData?.id && !studentData?.photoUrl && !studentData?.skipStudentMediaFetch;
  const studentIdForPhotoFetch = shouldFetchPhoto ? studentData?.id : undefined;
  const {
    imageUrl: authenticatedImageUrl,
    isLoading: photoLoading,
    error: imageError,
    hasPhoto,
  } = useStudentPhoto(studentIdForPhotoFetch);

  const shouldFetchSignature =
    !!studentData?.id && !studentData?.signatureUrl && !studentData?.skipStudentMediaFetch;
  const studentIdForSignatureFetch = shouldFetchSignature ? studentData?.id : undefined;
  const {
    signatureUrl: authenticatedSignatureUrl,
    isLoading: signatureLoading,
    error: signatureError,
    hasSignature,
  } = useStudentSignature(studentIdForSignatureFetch);

  // Fetch subjects by class ID
  const { data: subjects = [], isLoading: subjectsLoading } =
    useSubjectsByClassId(studentData?.schoolClassId || 0);

  // Create a mapping of subject codes to names
  const subjectCodeToName = React.useMemo(() => {
    const mapping: { [key: string]: string } = {};
    subjects.forEach((subject: any) => {
      mapping[subject.subjectCode] = subject.name;
    });
    return mapping;
  }, [subjects]);

  // Get subject names from student's subject codes
  const getSubjectNames = React.useCallback(
    (subjectCodes: string | string[]) => {
      if (!subjectCodes) return [];
      const codes = Array.isArray(subjectCodes) ? subjectCodes : [subjectCodes];
      return codes
        .map((code) => subjectCodeToName[code] || code)
        .filter(Boolean);
    },
    [subjectCodeToName]
  );

  // Language subjects list
  const languageSubjects = [
    "Hindi",
    "English",
    "Marathi",
    "Gujrati",
    "Punjabi",
    "Urdu",
    "Sindhi",
    "Sanskrit",
  ];

  // School data fetching
  const { user, isSchoolAdmin } = useAuth();

  const shouldFetchAdminSchoolId = !!user && isSchoolAdmin() && !user.schoolId;
  const adminSchoolIdQuery = useSchoolIdForAdmin(
    shouldFetchAdminSchoolId ? user?.id : undefined
  );
  const effectiveSchoolId =
    user?.schoolId ?? adminSchoolIdQuery.data ?? studentData?.schoolId;
  const { useSchoolById } = useSchools();
  const { data: schoolData, isLoading: schoolLoading } =
    useSchoolById(effectiveSchoolId);

  const [form, setForm] = useState({
    // Applicant/meta
    applicantTypeCode: "",
    isMPResident: false,
    cwsnCode: "",
    section: "",
    previousYear: "",
    previousRollNo: "",
    previousBoard: "",
    previousResult: "",
    previousGrade: "",

    // Names
    candidateName: "",
    fatherName: "",
    motherName: "",

    // DOB & gender
    dateOfBirth: { day: "", month: "", year: "" },
    dateOfBirthInWords: "",
    gender: "",

    // Student details
    tenthRollNo: [] as string[],
    tenthYear: [] as string[],
    mediumCode: "",

    // Codes & location
    blockName: "",
    districtName: "",
    classSubjectCode: "",
    examCenterCode: "",

    // Auto-filled codes from school data
    districtCode: "",
    vikasKhandCode: "",
    shalaAgrasenCode: "",
    namankanCode: "",

    // Auto-filled institution data
    currentDate: "",
    institutionCode: "",
    institutionName: "",

    // Languages
    firstLanguage: { first: "", second: "" },
    secondLanguage: { first: "", second: "" },
    thirdLanguage: { first: "", second: "" },

    // IDs
    ssmid: "",
    sambalNo: "",
    organizationNo: "",
    apparId: "",

    // Address (backend DTO field names)
    houseNo: "",
    street: "",
    area: "",
    city: "",
    district: "",
    mobileNo: "",
    pinCode: "",

    // Misc / flags
    caste: "",
    q11: "",
    q12: "",
    q13: "",
    q14: "",
    q15: "",
    q16: "",
    q17: "",
    declarationSignature: "",
    principalSeal: "",
    principalDate: "",
    principalSignature: "",
    deaf: "",
    dumb: "",

    // Subjects
    subjects: [] as string[],
  });

  /** Populate date and institution fields from school data */
  useEffect(() => {
    if (!schoolData) return;

    const schoolCode = schoolData.schoolCode || "";
    const schoolName = schoolData.nameofSchool || "";

    // Get current date in DD/MM/YYYY format
    const today = new Date();
    const day = today.getDate().toString().padStart(2, "0");
    const month = (today.getMonth() + 1).toString().padStart(2, "0");
    const year = today.getFullYear();
    const currentDate = `${day}/${month}/${year}`;

    setForm((prev) => ({
      ...prev,
      // Current date in DD/MM/YYYY format
      currentDate: currentDate,
      // Institution code: 6-digit school code
      institutionCode: schoolCode,
      // Institution name: school name
      institutionName: schoolName,
      // District Code: Use actual districtCode from school data
      districtCode: schoolData.districtCode || schoolCode.slice(0, 2).padEnd(2, "0"),
      // Vikas Khand Code: Use actual blockCode from school data
      vikasKhandCode: schoolData.blockCode || schoolCode.slice(0, 4).padEnd(4, "0"),
      // Shala Agrasen Sanstha Code: Full 6-digit school code
      shalaAgrasenCode: schoolCode.slice(0, 6).padEnd(6, "0"),
    }));
  }, [schoolData]);

  /** Populate Namankan Code from student enrollment number */
  useEffect(() => {
    if (!studentData) return;

    const enrollmentNo = studentData.enrolmentNo || "";
    // Pad to 12 digits or truncate if longer
    const namankanCode = enrollmentNo;

    setForm((prev) => ({
      ...prev,
      namankanCode: namankanCode,
    }));
  }, [studentData]);

  // Categorize subjects into language and non-language
  const categorizeSubjects = React.useCallback(() => {
    const subjectNames = getSubjectNames(form.subjects);
    const languageSubjectsFound: Array<{
      name: string;
      code: string;
      index: number;
    }> = [];
    const nonLanguageSubjects: Array<{
      name: string;
      code: string;
      index: number;
    }> = [];

    subjectNames.forEach((subjectName, index) => {
      const isLanguage = languageSubjects.some((lang) =>
        subjectName.toLowerCase().includes(lang.toLowerCase())
      );

      if (isLanguage) {
        languageSubjectsFound.push({
          name: subjectName,
          code: form.subjects[index] || "",
          index,
        });
      } else {
        nonLanguageSubjects.push({
          name: subjectName,
          code: form.subjects[index] || "",
          index,
        });
      }
    });

    return { languageSubjectsFound, nonLanguageSubjects };
  }, [form.subjects, getSubjectNames, studentData]);

  const { languageSubjectsFound, nonLanguageSubjects } = categorizeSubjects();

  // For 12th exam form subject layout: first/second language and first–third/additional subjects
  const firstLanguage = languageSubjectsFound[0];
  const secondLanguage = languageSubjectsFound[1];

  const subjectRowsForUi = nonLanguageSubjects.slice(0, 4);
  const firstSubject = subjectRowsForUi[0];
  const secondSubject = subjectRowsForUi[1];
  const thirdSubject = subjectRowsForUi[2];
  const additionalSubject = subjectRowsForUi[3];

  /** Populate form from studentData (once / on change) */
  useEffect(() => {
    if (!studentData) return;

    const fullName = studentData.studentName || "";
    const fatherName = studentData.fatherName || "";
    const motherName = studentData.motherName || "";

    // Parse DOB from array or string
    let dob = { day: "", month: "", year: "" };
    if (studentData.dateOfBirth) {
      if (Array.isArray(studentData.dateOfBirth)) {
        const [y, m, d] = studentData.dateOfBirth;
        dob = {
          day: String(d).padStart(2, "0"),
          month: String(m).padStart(2, "0"),
          year: String(y),
        };
      } else {
        const date = new Date(studentData.dateOfBirth);
        dob = {
          day: String(date.getDate()).padStart(2, "0"),
          month: String(date.getMonth() + 1).padStart(2, "0"),
          year: String(date.getFullYear()),
        };
      }
    }

    // Gender mapping
    let genderCode = "";
    const g = (studentData.gender || "").toLowerCase();
    if (g === "m" || g === "male") genderCode = "1";
    else if (g === "f" || g === "female") genderCode = "2";

    // Caste mapping
    let casteCode = "";
    const c = (studentData.caste || "").toLowerCase();
    if (c === "sc" || c.includes("scheduled caste")) casteCode = "1";
    else if (c === "st" || c.includes("scheduled tribe")) casteCode = "2";
    else if (c === "obc" || c.includes("backward")) casteCode = "3";
    else casteCode = "4";

    // Medium mapping
    let mediumCode = "";
    const m = (studentData.medium || "").toLowerCase();
    if (m.includes("hindi")) mediumCode = "1";
    else if (m.includes("english")) mediumCode = "2";
    else if (m.includes("urdu")) mediumCode = "3";

    // Date in words
    const dateInWords = convertDateToHindiWords(formatDateFromForm(dob));

    setForm((prev) => ({
      ...prev,
      candidateName: fullName,
      fatherName,
      motherName,
      dateOfBirth: dob,
      dateOfBirthInWords: dateInWords,
      gender: genderCode,
      caste: casteCode,
      mediumCode,

      // Address/IDs
      ssmid: studentData.sssmid || "",
      mobileNo: studentData.mobileNo || "",
      houseNo: studentData.houseNo || "",
      street: studentData.street || "",
      area: studentData.area || "",
      city: studentData.city || "",
      district: studentData.district || "",
      pinCode: studentData.pinCode || "",
      apparId: studentData.apparId || "",
      section: studentData.section || "",

      // Subjects - populate from student data
      subjects: Array.isArray(studentData.subjects)
        ? studentData.subjects
        : studentData.subjects
        ? [studentData.subjects]
        : [],
    }));
  }, [studentData]);

  /** Recompute date in words if typed manually */
  useEffect(() => {
    const { day, month, year } = form.dateOfBirth;
    if (day && month && year) {
      const dateString = formatDateFromForm(form.dateOfBirth);
      const inWords = convertDateToHindiWords(dateString);
      if (inWords !== form.dateOfBirthInWords) {
        setForm((p) => ({ ...p, dateOfBirthInWords: inWords }));
      }
    }
  }, [form.dateOfBirth, form.dateOfBirthInWords]);

  const updateForm = useCallback((field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  /** ---------------- Download helpers (single page) ---------------- */
  const downloadAsImage = async () => {
    if (!templateRef.current) return;
    setIsDownloadingImage(true);
    setCaptureMode(true);
    try {
      await (document as any).fonts?.ready?.catch(() => {});
      await new Promise((r) => setTimeout(r, 80));
      const canvas = await html2canvas(templateRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        background: "#ffffff",
        logging: false,
        scale: 2,
        width: templateRef.current.scrollWidth,
        height: templateRef.current.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: templateRef.current.scrollWidth,
        windowHeight: templateRef.current.scrollHeight,
      } as any);
      const link = document.createElement("a");
      link.download = `exam-form-${studentData?.studentName || "student"}-${
        new Date().toISOString().split("T")[0]
      }.png`;
      link.href = canvas.toDataURL("image/png", 1.0);
      link.click();
    } catch (e) {
      alert("Failed to download image. Please try again.");
    } finally {
      setCaptureMode(false);
      setIsDownloadingImage(false);
    }
  };

  const downloadAsPdfFromImage = async () => {
    if (!templateRef.current) return;
    setIsDownloadingPdfFromImage(true);
    setCaptureMode(true);
    try {
      await (document as any).fonts?.ready?.catch(() => {});
      await new Promise((r) => setTimeout(r, 80));
      const element = templateRef.current;
      const opt = {
        margin: [0, 0, 0, 0],
        filename: `exam-form-${studentData?.studentName || "student"}-${
          new Date().toISOString().split("T")[0]
        }.pdf`,
        image: { type: "jpeg", quality: 1.0 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          width: element.scrollWidth,
          height: element.scrollHeight,
          scrollX: 0,
          scrollY: 0,
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight,
          logging: false,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: false,
          putOnlyUsedFonts: true,
        },
        pagebreak: { mode: ["css", "legacy"], before: ".page-break" },
      };
      await html2pdf().set(opt).from(element).save();
    } catch (e) {
      alert("Failed to download PDF. Please try again.");
    } finally {
      setCaptureMode(false);
      setIsDownloadingPdfFromImage(false);
    }
  };

  if (!studentData) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No student data provided
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please select a student to view the exam form.
        </Typography>
      </Box>
    );
  }

  /** ----------------------------------------------------------------
   *  Render
   *  ---------------------------------------------------------------- */
  return (
    <ThemeProvider theme={theme}>
      {/* IMPORTANT: data-pdf-root so off-screen renderer targets this node */}
      <Paper
        ref={templateRef}
        data-pdf-root
        sx={{
          width: "210mm",
          minHeight: "297mm",
          padding: "0mm",
          // border: "1px solid #000",
          margin: "0 auto",
          position: "relative",
          backgroundColor: "white",
          "*": { boxSizing: "border-box" },
          // Ensure complex-script shaping and crisp capture
          fontFeatureSettings: "'liga' 1, 'clig' 1, 'kern' 1",
          WebkitFontSmoothing: "antialiased",
          textRendering: "optimizeLegibility",
          "& .avoid-break": { breakInside: "avoid", pageBreakInside: "avoid" },
          "& .allow-break": { breakInside: "auto", pageBreakInside: "auto" },
          "@media print": {
            boxShadow: "none",
            backgroundColor: "white",
            margin: 0,
            padding: "3mm",
          },
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 1, backgroundColor: "white" }}>
          <Box
            sx={{
              width: "20mm",
              height: "20mm",
              margin: "0 auto 0mm",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* CORS-safe to avoid tainted canvas */}
            <img
              src={process.env.PUBLIC_URL + "/tenthExamFormLogo.png"}
              alt="MP Board Logo"
              crossOrigin="anonymous"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </Box>
          <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
            माध्यमिक शिक्षा मण्डल, मध्यप्रदेश, भोपाल
          </Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
          आवेदन पत्र हायर सेकंडरी सर्टिफिकेट परीक्षा
          </Typography>
        </Box>

        {/* ----------------- FORM CONTENT (matches your layout) ----------------- */}
        <Grid container spacing={0.2} sx={{ backgroundColor: "white", mx: 0 }}>
          {/* Section 1 (Applicant Type + Codes) */}
          <Grid item xs={12}>
            <Box
              sx={{ px: 0.3, position: "relative", backgroundColor: "white" }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", mb: 0 }}>
                <Box sx={{ width: "36mm", flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.6,
                      mt: 0.5,
                    }}
                  >
                    <Label sx={{ fontSize: "13px" }}>1. आवेदक का प्रकार</Label>
                    <Cell width="16mm" height="10mm">
                      <input
                        type="text"
                        value={form.applicantTypeCode}
                        onChange={(e) =>
                          updateForm("applicantTypeCode", e.target.value)
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          outline: "none",
                          textAlign: "center",
                          fontSize: "10px",
                          fontWeight: "bold",
                          background: "transparent",
                        }}
                        maxLength={1}
                      />
                    </Cell>
                  </Box>
                </Box>

                <Box sx={{ flex: 1, ml: 1 }}>
                  <Box
                    sx={{
                      border: "1px solid #000",
                      p: 0.5,
                      mb: 1,
                      backgroundColor: "white",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "14px", fontWeight: "bolder", mb: 0.5 }}
                    >
                      संबंधित कोड नम्बर ही खाने में भरिये
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-around",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                        नियमित-1
                      </Typography>
                      <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                        स्वाध्यायी-2
                      </Typography>
                      <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                        नियमित (12वीं फेल)-3
                      </Typography>
                      <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                        स्वाध्यायी (12वीं फेल)-4
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-around",
                        flexWrap: "wrap",
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                        श्रेणी सुधार (नियमित)-5
                      </Typography>
                      <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                        श्रेणी सुधार (स्वाध्यायी)-6
                      </Typography>
                      <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                        ओरियेंटल उम्मीदवार (इलाहाबाद)-11
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Residency & codes row */}
              <Box
                className="avoid-break"
                sx={{
                  display: "flex",
                  justifyContent: "space-around",
                  alignItems: "center",
                  mb: 1,
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Label sx={{ fontSize: "14px", fontWeight: "bolder" }}>
                    मध्यप्रदेश के मूलनिवासी
                  </Label>
                  <Typography
                    sx={{ fontSize: "12px", ml: 1, fontWeight: "bold" }}
                  >
                    हाँ/नही
                  </Typography>
                  <Tick
                    label=""
                    checked={form.isMPResident}
                    onChange={(checked) => updateForm("isMPResident", checked)}
                  />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Label sx={{ fontSize: "14px", fontWeight: "bolder" }}>
                    CWSN Code
                  </Label>
                  <Box
                    sx={{
                      width: "20mm",
                      height: "6mm",
                      border: "1px solid #000",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <input
                      type="text"
                      value={studentData?.cwsnCode || ""}
                      readOnly
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                        outline: "none",
                        textAlign: "center",
                        fontSize: "13px",
                        fontWeight: "bold",
                        background: "transparent",
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: "10px" }}>
                    (Ref.Point 11)
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Label sx={{ fontSize: "14px", fontWeight: "bolder" }}>
                    Section
                  </Label>
                  <Box
                    sx={{
                      width: "18mm",
                      height: "6mm",
                      border: "1px solid #000",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <input
                      type="text"
                      value={form.section}
                      onChange={(e) => updateForm("section", e.target.value)}
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                        outline: "none",
                        textAlign: "center",
                        fontSize: "13px",
                        fontWeight: "bold",
                        background: "transparent",
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Previous Examination Details */}
              <Box sx={{ mb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    sx={{ fontSize: "13px", mb: 1, fontWeight: "bold" }}
                  >
                    क्या अर्हकारी परीक्षा अन्य बोर्ड/अन्य राज्य से उत्तीर्ण की
                    है, यदि हाँ तो बोर्ड एवं वर्ष की जानकारी अंकित करें।
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Label sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      वर्ष
                    </Label>
                    <Box
                      sx={{
                        width: "24mm",
                        height: "6mm",
                        border: "1px solid #000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <input
                        type="text"
                        value={form.previousYear}
                        onChange={(e) =>
                          updateForm("previousYear", e.target.value)
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          outline: "none",
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: "bold",
                          background: "transparent",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "start",
                      gap: 1,
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Label sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      रोल नम्बर
                    </Label>
                    <Box
                      sx={{
                        width: "35mm",
                        height: "6mm",
                        border: "1px solid #000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <input
                        type="text"
                        value={form.previousRollNo}
                        onChange={(e) =>
                          updateForm("previousRollNo", e.target.value)
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          outline: "none",
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: "bold",
                          background: "transparent",
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "start",
                      gap: 1,
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Label sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      बोर्ड का नाम
                    </Label>
                    <Box
                      sx={{
                        width: "70mm",
                        height: "6mm",
                        border: "1px solid #000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <input
                        type="text"
                        value={form.previousBoard}
                        onChange={(e) =>
                          updateForm("previousBoard", e.target.value)
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          outline: "none",
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: "bold",
                          background: "transparent",
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      flexDirection: "column",
                      justifyContent: "start",
                    }}
                  >
                    <Label sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      परिणाम
                    </Label>
                    <Box
                      sx={{
                        width: "35mm",
                        height: "6mm",
                        border: "1px solid #000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <input
                        type="text"
                        value={form.previousResult}
                        onChange={(e) =>
                          updateForm("previousResult", e.target.value)
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          outline: "none",
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: "bold",
                          background: "transparent",
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      flexDirection: "column",
                      justifyContent: "start",
                    }}
                  >
                    <Label sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      श्रेणी
                    </Label>
                    <Box
                      sx={{
                        width: "35mm",
                        height: "6mm",
                        border: "1px solid #000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <input
                        type="text"
                        value={form.previousGrade}
                        onChange={(e) =>
                          updateForm("previousGrade", e.target.value)
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          outline: "none",
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: "bold",
                          background: "transparent",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Section 2 – Candidate name */}
          <Grid item xs={12}>
            <Box
              sx={{ p: 0.7, position: "relative", backgroundColor: "white" }}
            >
              <Box sx={{ display: "flex", gap: 0.4, flexDirection: "column" }}>
                <Row label="2. आवेदक का पूर्ण नाम / FULL NAME OF CANDIDATE" />
                <CharBoxes
                  count={37}
                  value={form.candidateName.trim()}
                  onChange={(value) => updateForm("candidateName", value)}
                  printMode={isPrint}
                />
              </Box>
            </Box>
          </Grid>

          {/* Section 3 – Father */}
          <Grid item xs={12}>
            <Box
              sx={{ p: 0.7, position: "relative", backgroundColor: "white" }}
            >
              <Box sx={{ display: "flex", gap: 0.4, flexDirection: "column" }}>
                <Row label="3. पिता का पूर्ण नाम / FULL NAME OF FATHER" />
                <CharBoxes
                  count={37}
                  value={form.fatherName.trim()}
                  onChange={(value) => updateForm("fatherName", value)}
                  printMode={isPrint}
                />
              </Box>
            </Box>
          </Grid>

          {/* Section 4 – Mother */}
          <Grid item xs={12}>
            <Box
              sx={{ p: 0.7, position: "relative", backgroundColor: "white" }}
            >
              <Row label="4. माता का पूर्ण नाम / FULL NAME OF MOTHER" />
              <CharBoxes
                count={37}
                value={form.motherName.trim()}
                onChange={(value) => updateForm("motherName", value)}
                printMode={isPrint}
              />
            </Box>
          </Grid>

          {/* Section 5 – DOB + in words */}
          <Grid item xs={12}>
            <Box sx={{ p: 1, position: "relative", backgroundColor: "white" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex" }}>
                  <Box>
                    <Label>5. जन्म तिथि</Label>
                    <Label> (अंको में)</Label>
                  </Box>
                  <Box>
                    <DateBoxes
                      value={form.dateOfBirth}
                      onChange={(value) => updateForm("dateOfBirth", value)}
                      printMode={isPrint}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: "flex" }}>
                  <Box sx={{ ml: 8 }}>
                    <Label>जन्म तिथि</Label>
                    <Label>(शब्दों में)</Label>
                  </Box>
                  <Box>
                    {isPrint ? (
                      <Cell
                        width="300px"
                        height="30px"
                        sx={{ justifyContent: "flex-start", px: 1, ml: "10mm" }}
                      >
                        <PrintText sx={{ fontSize: "14px", fontWeight: 700 }}>
                          {form.dateOfBirthInWords}
                        </PrintText>
                      </Cell>
                    ) : (
                      <Input
                        value={form.dateOfBirthInWords}
                        readOnly
                        sx={{
                          width: "300px",
                          height: "30px",
                          fontSize: "14px",
                          border: "1px solid #000",
                          borderRadius: "2px",
                          marginLeft: "10mm",
                          px: 1,
                          "&:before": { borderBottom: "none" },
                          "&:after": { borderBottom: "none" },
                          "&:hover:not(.Mui-disabled):before": {
                            borderBottom: "none",
                          },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Section 6 – Codes + brief note area (trimmed) */}
          <Grid item xs={12}>
            <Box sx={{ p: 1, position: "relative", backgroundColor: "white" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box
                  className="avoid-break"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 4,
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.7,
                        alignItems: "center",
                      }}
                    >
                      <Label>6. जिला कोड</Label>
                      <CharBoxes
                        count={2}
                        value={form.districtCode}
                        onChange={(value) => updateForm("districtCode", value)}
                        printMode={isPrint}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.7,
                        alignItems: "center",
                      }}
                    >
                      <Label>विकास खण्ड कोड</Label>
                      <CharBoxes
                        count={4}
                        value={form.vikasKhandCode}
                        onChange={(value) =>
                          updateForm("vikasKhandCode", value)
                        }
                        printMode={isPrint}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.7,
                        alignItems: "center",
                      }}
                    >
                      <Label>शाला अग्रसेन संस्था कोड</Label>
                      <CharBoxes
                        count={6}
                        value={form.shalaAgrasenCode}
                        onChange={(value) =>
                          updateForm("shalaAgrasenCode", value)
                        }
                        printMode={isPrint}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.7,
                        alignItems: "center",
                      }}
                    >
                      <Label>नामांकन कोड</Label>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CharBoxes
                          count={14}
                          value={form.namankanCode}
                          onChange={(vlaue)=>updateForm("namankanCode", vlaue)}
                          printMode={isPrint}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Section 7 – Language & Diversified Group Subjects (12th class style) */}
          <Grid item xs={12}>
            <Box sx={{ px: 1, position: "relative", backgroundColor: "white" }}>
              <Typography
                sx={{ fontSize: "9", fontWeight: "bold", mb: 0.7 }}
              >
                7  भाषा एवं डायवर्सिफाईड ग्रुप के विषय (विषय कोड सूची अनुसार ही भरे)
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                {/* Left side – प्रथम / द्वितीय भाषा */}
                <Box sx={{ flex: 1, pr: 2 }}>
                  {/* प्रथम भाषा */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 0.5,
                      gap: 1,
                    }}
                  >
                    <Label sx={{ minWidth: "32mm" }}>प्रथम भाषा</Label>
                    <Box
                      sx={{
                        flex: 1,
                        borderBottom: "1px solid #000",
                        mr: 2,
                      }}
                    >
                      <input
                        type="text"
                        value={firstLanguage?.name || ""}
                        readOnly
                        style={{
                          border: "none",
                          outline: "none",
                          fontSize: "14px",
                          padding: "0px 4px",
                          background: "transparent",
                          width: "100%",
                        }}
                      />
                    </Box>
                    <MiniBoxes
                      count={3}
                      value={firstLanguage?.code || ""}
                      onChange={
                        firstLanguage
                          ? (value) => {
                              const newSubjects = [...form.subjects];
                              newSubjects[firstLanguage.index] = value;
                              updateForm("subjects", newSubjects);
                            }
                          : undefined
                      }
                      printMode={isPrint || !firstLanguage}
                    />
                  </Box>

                  {/* द्वितीय भाषा */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      gap: 1,
                    }}
                  >
                    <Label sx={{ minWidth: "32mm" }}>द्वितीय भाषा</Label>
                    <Box
                      sx={{
                        flex: 1,
                        borderBottom: "1px solid #000",
                        mr: 2,
                      }}
                    >
                      <input
                        type="text"
                        value={secondLanguage?.name || ""}
                        readOnly
                        style={{
                          border: "none",
                          outline: "none",
                          fontSize: "14px",
                          padding: "0px 4px",
                          background: "transparent",
                          width: "100%",
                        }}
                      />
                    </Box>
                    <MiniBoxes
                      count={3}
                      value={secondLanguage?.code || ""}
                      onChange={
                        secondLanguage
                          ? (value) => {
                              const newSubjects = [...form.subjects];
                              newSubjects[secondLanguage.index] = value;
                              updateForm("subjects", newSubjects);
                            }
                          : undefined
                      }
                      printMode={isPrint || !secondLanguage}
                    />
                  </Box>
                </Box>

                {/* Right side – मुख्य / अतिरिक्त विषय */}
                <Box sx={{ flex: 1, pl: 2, borderLeft: "1px solid #000" }}>
                  {/* प्रथम विषय */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 0.5,
                      gap: 1,
                    }}
                  >
                    <Label sx={{ minWidth: "32mm" }}>प्रथम विषय</Label>
                    <Box
                      sx={{
                        flex: 1,
                        borderBottom: "1px solid #000",
                        mr: 2,
                      }}
                    >
                      <input
                        type="text"
                        value={firstSubject?.name || ""}
                        readOnly
                        style={{
                          border: "none",
                          outline: "none",
                          fontSize: "14px",
                          padding: "0px 4px",
                          background: "transparent",
                          width: "100%",
                        }}
                      />
                    </Box>
                    <MiniBoxes
                      count={3}
                      value={firstSubject?.code || ""}
                      onChange={
                        firstSubject
                          ? (value) => {
                              const newSubjects = [...form.subjects];
                              newSubjects[firstSubject.index] = value;
                              updateForm("subjects", newSubjects);
                            }
                          : undefined
                      }
                      printMode={isPrint || !firstSubject}
                    />
                  </Box>

                  {/* द्वितीय विषय */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 0.5,
                      gap: 1,
                    }}
                  >
                    <Label sx={{ minWidth: "32mm" }}>द्वितीय विषय</Label>
                    <Box
                      sx={{
                        flex: 1,
                        borderBottom: "1px solid #000",
                        mr: 2,
                      }}
                    >
                      <input
                        type="text"
                        value={secondSubject?.name || ""}
                        readOnly
                        style={{
                          border: "none",
                          outline: "none",
                          fontSize: "14px",
                          padding: "0px 4px",
                          background: "transparent",
                          width: "100%",
                        }}
                      />
                    </Box>
                    <MiniBoxes
                      count={3}
                      value={secondSubject?.code || ""}
                      onChange={
                        secondSubject
                          ? (value) => {
                              const newSubjects = [...form.subjects];
                              newSubjects[secondSubject.index] = value;
                              updateForm("subjects", newSubjects);
                            }
                          : undefined
                      }
                      printMode={isPrint || !secondSubject}
                    />
                  </Box>

                  {/* तृतीय विषय */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 0.5,
                      gap: 1,
                    }}
                  >
                    <Label sx={{ minWidth: "32mm" }}>तृतीय विषय</Label>
                    <Box
                      sx={{
                        flex: 1,
                        borderBottom: "1px solid #000",
                        mr: 2,
                      }}
                    >
                      <input
                        type="text"
                        value={thirdSubject?.name || ""}
                        readOnly
                        style={{
                          border: "none",
                          outline: "none",
                          fontSize: "14px",
                          padding: "0px 4px",
                          background: "transparent",
                          width: "100%",
                        }}
                      />
                    </Box>
                    <MiniBoxes
                      count={3}
                      value={thirdSubject?.code || ""}
                      onChange={
                        thirdSubject
                          ? (value) => {
                              const newSubjects = [...form.subjects];
                              newSubjects[thirdSubject.index] = value;
                              updateForm("subjects", newSubjects);
                            }
                          : undefined
                      }
                      printMode={isPrint || !thirdSubject}
                    />
                  </Box>

                  {/* अतिरिक्त विषय */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Label sx={{ minWidth: "32mm" }}>अतिरिक्त विषय</Label>
                    <Box
                      sx={{
                        flex: 1,
                        borderBottom: "1px solid #000",
                        mr: 2,
                      }}
                    >
                      <input
                        type="text"
                        value={additionalSubject?.name || ""}
                        readOnly
                        style={{
                          border: "none",
                          outline: "none",
                          fontSize: "14px",
                          padding: "0px 4px",
                          background: "transparent",
                          width: "100%",
                        }}
                      />
                    </Box>
                    <MiniBoxes
                      count={3}
                      value={additionalSubject?.code || ""}
                      onChange={
                        additionalSubject
                          ? (value) => {
                              const newSubjects = [...form.subjects];
                              newSubjects[additionalSubject.index] = value;
                              updateForm("subjects", newSubjects);
                            }
                          : undefined
                      }
                      printMode={isPrint || !additionalSubject}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Special note under the box, as in board format */}
              <Box sx={{ mt: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: "bold",
                    lineHeight: 1.3,
                  }}
                >
                  विशेष टिप :- नेत्रहीन मूक-बधिर एवं स्पास्टिक सेरेब्रल पाल्सी
                  (मानसिक रूप से विकलांग) वर्ग के विद्यार्थियों को विशिष्ट भाषा से
                  छूट रहेगी। विद्यार्थी हिन्दी (सामान्य) अथवा अंग्रेजी (सामान्य)
                  में से कोई एक भाषा का चयन (पाठ्यक्रमानुसार) कर सकते हैं।
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Section 8 – SSMID + Sambal No */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", backgroundColor: "white" }}>
              <Box sx={{ p: 1, position: "relative" }}>
                <Row label="समग्र आई.डी. (SSMID)" />
                <CharBoxes
                  count={14}
                  value={form.ssmid}
                  onChange={(v) => updateForm("ssmid", v)}
                  printMode={isPrint}
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={{ fontWeight: "Semi-bold" }}>
                  संबल क्र.
                </Typography>
                <Box
                  sx={{
                    width: "60mm",
                    height: "6mm",
                    border: "1px solid #000",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <input
                    type="text"
                    value={studentData?.sambalKramank || ""}
                    readOnly
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      outline: "none",
                      textAlign: "center",
                      fontSize: "13px",
                      fontWeight: "bold",
                      background: "transparent",
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Section 9 – Address + Photo/Signature boxes */}
          <Grid item xs={12}>
            <Box sx={{ p: 1, position: "relative", backgroundColor: "white" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "9", fontWeight: "bold" }}>
                    9. पता / Address
                  </Typography>

                  <Box sx={{ mb: 0.7 }}>
                    <Label>House No.</Label>
                    <CharBoxes
                      count={27}
                      value={form.houseNo}
                      onChange={(v) => updateForm("houseNo", v)}
                      printMode={isPrint}
                    />
                  </Box>

                  <Box sx={{ mb: 0.7 }}>
                    <Label>Colony</Label>
                    <CharBoxes
                      count={27}
                      value={form.street}
                      onChange={(v) => updateForm("street", v)}
                      printMode={isPrint}
                    />
                  </Box>

                  <Box sx={{ mb: 0.7 }}>
                    <Label>Town</Label>
                    <CharBoxes
                      count={27}
                      value={form.city}
                      onChange={(v) => updateForm("city", v)}
                      printMode={isPrint}
                    />
                  </Box>

                  <Box sx={{ mb: 0.7 }}>
                    <Label>Block</Label>
                    <CharBoxes
                      count={27}
                      value={form.district}
                      onChange={(v) => updateForm("district", v)}
                      printMode={isPrint}
                    />
                  </Box>

                  <Box sx={{ mb: 0.7 }}>
                    <Label>District</Label>
                    <CharBoxes
                      count={27}
                      value={form.district}
                      onChange={(v) => updateForm("district", v)}
                      printMode={isPrint}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 0.7,
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Label>Phone No.</Label>
                      <CharBoxes
                        count={10}
                        value={form.mobileNo}
                        boxSizeMm={5}
                        onChange={(v) => updateForm("mobileNo", v)}
                        printMode={isPrint}
                      />
                    </Box>
                    <Box>
                      <Label>APAAR-ID</Label>
                      <CharBoxes
                        count={12}
                        value={form.apparId}
                        boxSizeMm={5}
                        onChange={(v) => updateForm("apparId", v)}
                        printMode={isPrint}
                      />
                    </Box>
                    <Box>
                      <Label>Pin</Label>
                      <CharBoxes
                        count={6}
                        value={form.pinCode}
                        boxSizeMm={5}
                        onChange={(v) => updateForm("pinCode", v)}
                        printMode={isPrint}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box
                  className="avoid-break"
                  sx={{
                    border: "1px solid #000",
                    p: 1,
                    textAlign: "center",
                    ml: 2,
                    mr: 2,
                    backgroundColor: "white",
                  }}
                >
                  <Typography sx={{ fontSize: "8px", mb: 0.7 }}>
                    परीक्षार्थी का स्वयं का नाम, निगमनांक, डिजिटल फोटो
                  </Typography>
                  <Box
                    sx={{
                      width: "35mm",
                      height: "40mm",
                      border: "1px solid #ccc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "8px",
                      color: "#666",
                      mb: 0.7,
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {(() => {
                      const effectivePhotoUrl = studentData?.photoUrl || authenticatedImageUrl;
                      const effectivePhotoLoading = shouldFetchPhoto ? photoLoading : false;
                      const effectivePhotoError = shouldFetchPhoto ? imageError : false;
                      if (effectivePhotoLoading) return <CircularProgress size={20} />;
                      if (effectivePhotoUrl && !effectivePhotoError) {
                        return (
                      <img
                        src={effectivePhotoUrl}
                        alt="Student Photo"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          position: "absolute",
                          top: 0,
                          left: 0,
                        }}
                      />
                        );
                      }
                      return (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          color: "#999",
                        }}
                      >
                        <PersonIcon sx={{ fontSize: "24px", mb: 0.5 }} />
                        <Typography
                          sx={{ fontSize: "8px", textAlign: "center" }}
                        >
                          No Photo
                        </Typography>
                      </Box>
                      );
                    })()}
                  </Box>
                  <Box
                    sx={{
                      width: "35mm",
                      height: "15mm",
                      border: "1px solid #ccc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "8px",
                      color: "#666",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {(() => {
                      const effectiveSigUrl = studentData?.signatureUrl || authenticatedSignatureUrl;
                      const effectiveSigLoading = shouldFetchSignature ? signatureLoading : false;
                      const effectiveSigError = shouldFetchSignature ? signatureError : false;
                      if (effectiveSigLoading) return <CircularProgress size={16} />;
                      if (effectiveSigUrl && !effectiveSigError) {
                        return (
                      <img
                        src={effectiveSigUrl}
                        alt="Student Signature"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                        );
                      }
                      return (
                      <Typography sx={{ fontSize: "8px", textAlign: "center" }}>
                        Signature
                      </Typography>
                      );
                    })()}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Tip / Notes */}
        <Box className="allow-break" sx={{ p: 2, bgcolor: "white" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
          >
            टीप:-
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: "14px",
              fontWeight: "Semi-bold",
              whiteSpace: "normal",
              wordWrap: "break-word",
              lineHeight: 1.4,
            }}
          >
            1. जिला कोड, विकास खण्ड कोड, विषयों के कोड एवं अन्य कोड मण्डल द्वारा
            प्रेषित एवं वेबसाईट पर उपलब्ध विषय कोड सूची के अनुसार ही भरे जायें।
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "14px",
              fontWeight: "Semi-bold",
              whiteSpace: "normal",
              wordWrap: "break-word",
              lineHeight: 1.4,
            }}
          >
            2. स्वाध्यायी परीक्षार्थी जिनके पास किसी भी बोर्ड द्वारा जारी
            नामांकन नहीं है तथा वे बोर्ड की परीक्षा में प्रथम बार स्वाध्यायी
            परीक्षार्थी के रूप में शामिल हो रहे हैं, वे ग्राहयता/नामांकन शुल्क
            परीक्षा शुल्क के साथ <b>online</b> जमा करेंगे।
          </Typography>
        </Box>

        {/* Section 10 – Gender + Caste */}
        <Box px={2} sx={{ backgroundColor: "white" }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: "13px" }}>
            10. छात्र का अन्य विवरण :-
          </Typography>

          <Grid container spacing={2} sx={{ fontSize: "12px" }}>
            {/* Gender */}
            <Grid item xs={5} sm={3}>
              <Box border={1} px={2} py={1} height="100%">
                <Label
                  sx={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    display: "flex",
                    justifyContent: "center",
                    mt: 1,
                  }}
                >
                  लिंग
                </Label>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Tick
                    label="बालक - 1"
                    checked={form.gender === "1"}
                    onChange={(c) => updateForm("gender", c ? "1" : "")}
                  />
                  <Tick
                    label="बालिका - 2"
                    checked={form.gender === "2"}
                    onChange={(c) => updateForm("gender", c ? "2" : "")}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Caste */}
            <Grid item xs={12} sm={4.5}>
              <Box border={1} px={2} height="100%">
                <Typography align="center" sx={{ fontWeight: "bold", mt: 1 }}>
                  जाति
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box>
                    <Tick
                      label="अनुसूचित जाति - 1"
                      checked={form.caste === "1"}
                      onChange={(c) => updateForm("caste", c ? "1" : "")}
                    />
                    <Tick
                      label="अनुसूचित जनजाति - 2"
                      checked={form.caste === "2"}
                      onChange={(c) => updateForm("caste", c ? "2" : "")}
                    />
                  </Box>
                  <Box>
                    <Tick
                      label="पिछड़ा वर्ग - 3"
                      checked={form.caste === "3"}
                      onChange={(c) => updateForm("caste", c ? "3" : "")}
                    />
                    <Tick
                      label="सामान्य - 4"
                      checked={form.caste === "4"}
                      onChange={(c) => updateForm("caste", c ? "4" : "")}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Note */}
            <Grid item xs={12} sm={4.5}>
              <Typography sx={{ mt: 1, fontSize: "12px" }}>
                बिन्दु क्र. 11 से 17 तक के प्रकरणों में सक्षम अधिकारी द्वारा
                सत्यापित प्रमाण-पत्र संलग्न करना आवश्यक है ।
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Section 11–18 (trimmed to main toggles/inputs for brevity) */}
        <Box
          className="allow-break"
          px={2}
          mt={1}
          sx={{ backgroundColor: "white" }}
        >
          {[
            {
              key: "q11",
              text: "11. क्या आप मण्डल द्वारा मान्यता प्राप्त शिक्षण संस्थाओं के सेवा निवृत्त शिक्षकों एवं राष्ट्रपति द्वारा पुरस्कृत मध्यप्रदेश के शिक्षक/शिक्षिका के पुत्र/पुत्री है?",
            },
            {
              key: "q12",
              text: "12. क्या आप मण्डल के अधिकारी/कर्मचारी के पुत्र/पुत्री है?",
            },
            {
              key: "q13",
              text: "13. आप मण्डल द्वारा मान्यता प्राप्त शिक्षण संस्थाओं में कार्यरत शिक्षक/शिक्षिका के पुत्र/पुत्री है?",
            },
            {
              key: "q14",
              text: "14. क्या आप ऐसी महिला परीक्षार्थी है, जिनके अभिभावकों की सभी स्रोतों से वार्षिक आय रू. 15,000/- से अधिक नहीं है? (केवल महिला परीक्षार्थी के लिए)",
            },
            {
              key: "q15",
              text: "15. क्या आपके परिवार की समस्त स्रोतों से वार्षिक आय रु. 1,00,000/- (एक लाख) से अधिक नहीं है? (केवल SC छात्रों के लिए)",
            },
            {
              key: "q16",
              text: "16. क्या आपके परिवार के समस्त स्रोतों से वार्षिक आय रू. 2,50,000/- (दो लाख पचास हजार) से अधिक नहीं है? (केवल ST छात्रों के लिए)",
            },
          ].map((q) => (
            <Box key={q.key} mb={1}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "Semi-bold",
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                  lineHeight: 1.4,
                }}
              >
                {q.text}{" "}
                <Tick
                  label="हाँ"
                  checked={(form as any)[q.key] === "yes"}
                  onChange={(c) => updateForm(q.key, c ? "yes" : "no")}
                />
                <Tick
                  label="नहीं"
                  checked={(form as any)[q.key] === "no"}
                  onChange={(c) => updateForm(q.key, c ? "no" : "yes")}
                />
              </Typography>
            </Box>
          ))}

          <Box mb={1} sx={{ display: "flex", gap: 4 }}>
            <Typography sx={{ fontSize: "14px", fontWeight: "Semi-bold" }}>
              17. शाला की प्रवेश सूची में परीक्षार्थी का सरल क्रमांक
            </Typography>

            <Input
              value={form.q17}
              onChange={(e) => updateForm("q17", e.target.value)}
              sx={{ border: "1px solid #000", width: 130, height: 30 }}
            />
            <Typography sx={{ fontSize: "12px", fontWeight: "bold", mb: 1 }}>
              (केवल नियमित परीक्षार्थीओं के लिए)
            </Typography>
          </Box>

          <Box mb={1}>
            <Box sx={{ display: "flex", gap: 3 }}>
              <Typography sx={{ fontSize: "14px", fontWeight: "Semi-bold" }}>
                18. संलग्न दस्तावेज
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", fontWeight: "bold", mb: 1 }}
              >
                (केवल अर्हकारी परीक्षा अन्य बोर्ड से उत्तीर्ण छात्रों के लिए)
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ fontSize: "14px", fontWeight: "Semi-bold" }}
            >
              (अ) स्थानान्तरण प्रमाण पत्र।
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "14px", fontWeight: "Semi-bold" }}
            >
              (ब) कक्षा 9वीं की अंकसूची।
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "14px", fontWeight: "Semi-bold" }}
            >
              (स) प्रवर्जन प्रमाण-पत्र।
            </Typography>
          </Box>
        </Box>

        {/* Declaration */}
        <Box
          className="allow-break"
          px={2}
          sx={{
            backgroundColor: "white",
            borderTop: "1px solid #000",
            borderBottom: "1px solid #000",
          }}
        >
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontSize: "14px",
              fontWeight: "Bold",
              display: "flex",
              justifyContent: "center",
            }}
          >
            परीक्षार्थी की घोषणा
          </Typography>
          <Typography
            variant="body2"
            gutterBottom
            sx={{
              fontSize: "14px",
              fontWeight: "Semi-bold",
              whiteSpace: "normal",
              wordWrap: "break-word",
              lineHeight: 1.4,
              textAlign: "justify",
            }}
          >
            मेरे द्वारा दी गई उपरोक्त जानकारी सत्य है यदि इसमें किसी प्रकार की
            असत्य जानकारी पाई गई तो मेरा परीक्षा आवेदन एवं परीक्षा परिणाम कभी भी
            निरस्त किया जा सकता है।
          </Typography>
          <Box display="flex" justifyContent="flex-end">
            <Box textAlign="center">
              <Typography
                variant="body2"
                sx={{ fontSize: "14px", fontWeight: "Bold", mt: 2 }}
              >
                परीक्षार्थी के हस्ताक्षर
              </Typography>
              {/* <Input
                value={form.declarationSignature}
                onChange={(e) =>
                  updateForm("declarationSignature", e.target.value)
                }
                sx={{ border: "1px solid #000", width: 150, height: 33 }}
              /> */}
            </Box>
          </Box>
        </Box>

        {/* Principal Certification */}
        <Box
          className="allow-break"
          px={2}
          sx={{
            backgroundColor: "white",
            borderTop: "1px solid #000",
            borderBottom: "1px solid #000",
            mt: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontSize: "14px",
              fontWeight: "Bold",
              display: "flex",
              justifyContent: "center",
            }}
          >
            प्राचार्य / अग्रेषण संस्था प्राचार्य का प्रमाणीकरण
          </Typography>
          <Typography
            variant="body2"
            gutterBottom
            sx={{ fontSize: "14px", fontWeight: "Semi-bold" }}
          >
            मैं संबंधित दस्तावेजों की जाँच उपरांत प्रमाणित करता हूँ कि छात्र
            परीक्षा में प्रविष्ट होने हेतु निर्धारित मापदण्ड पूर्ण करता है।
          </Typography>

          <Grid
            container
            spacing={2}
            // sx={{ display: "flex", justifyContent: "space-between"}}
          >
            <Grid item>
              <Box>
                <Typography variant="body2" sx={{ fontSize: "14px" }}>
                  दिनांक:
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                  mt: 1,
                }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: "14px" ,width: "167px"}}>
                    संस्था क्रमांक : {form.institutionCode}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "14px", width: "445px" }}
                  >
                    संस्था का नाम : {form.institutionName}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "14px", fontWeight: "Bold" }}
                  >
                    हस्ताक्षर पदमुद्रा सहित
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* CWSN Notes */}
        <Box
          className="allow-break"
          px={2}
          py={1}
          sx={{ backgroundColor: "white" }}
        >
          <Typography
            variant="body2"
            sx={{ fontStyle: "italic", fontSize: "14px", fontWeight: "Bold" }}
          >
            19. CWSN (Children With Special Need):
          </Typography>
          <Box sx={{ ml: 2, mt: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: "12px", fontWeight: "Bold" }}
            >
              1. Loco Motor Disability/Leprosy Cured Person, Cerebral Palsy
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "12px", fontWeight: "Bold" }}
            >
              2. Visual Impairment, Blindness, Low vision
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "12px", fontWeight: "Bold" }}
            >
              3. Hearing Impairment, Deaf/Hard of hearing
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "12px", fontWeight: "Bold" }}
            >
              4. Speech & Language Disability
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "12px", fontWeight: "Bold" }}
            >
              5. Specific Learning Disabilities
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "12px", fontWeight: "Bold" }}
            >
              6. Autism Spectrum Disorder
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "12px", fontWeight: "Bold" }}
            >
              7. Chronic Neurological Conditions
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Download Buttons (hidden when renderForPDF is true) */}
      {!renderForPDF && (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Image />}
              disabled={isDownloadingImage || isDownloadingPdfFromImage}
              onClick={downloadAsImage}
              sx={{
                px: 3,
                py: 1.5,
                fontSize: 14,
                fontWeight: 600,
                minWidth: "200px",
                backgroundColor: "#9c27b0",
                "&:hover": { backgroundColor: "#7b1fa2" },
              }}
            >
              {isDownloadingImage ? "Generating Image..." : "Download as Image"}
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={<Download />}
              disabled={isDownloadingImage || isDownloadingPdfFromImage}
              onClick={downloadAsPdfFromImage}
              sx={{
                px: 3,
                py: 1.5,
                fontSize: 14,
                fontWeight: 600,
                minWidth: "200px",
                background: "linear-gradient(135deg, #0168EA 0%, #3790ff 100%)",
                boxShadow: "0 4px 12px rgba(1, 104, 234, 0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #0056d3 0%, #2f7de8 100%)",
                  boxShadow: "0 6px 16px rgba(1, 104, 234, 0.4)",
                },
              }}
            >
              {isDownloadingPdfFromImage
                ? "Generating PDF..."
                : "Download as PDF"}
            </Button>
          </Box>
        </Box>
      )}
    </ThemeProvider>
  );
};

export default TwelfthExamFormTemplate;
